import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * TerrainFloor — Lightweight Three.js wireframe terrain that sits below the hero.
 * - Non-interactive (no mouse handlers, no camera moves)
 * - Pauses when out of viewport (IntersectionObserver)
 * - Pauses when tab is hidden
 * - Capped pixel ratio + moderate mesh density for smooth 60fps
 */
export function TerrainFloor() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ─── Scene setup ───────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      55,
      mount.clientWidth / mount.clientHeight,
      0.1,
      100
    );
    camera.position.set(0, 1.2, 4.2);
    camera.lookAt(0, 0.6, -8);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "low-power",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // ─── Plane geometry (moderate, smooth without overload) ───────────────
    const SEGMENTS_X = 96;
    const SEGMENTS_Z = 64;
    const planeGeom = new THREE.PlaneGeometry(40, 28, SEGMENTS_X, SEGMENTS_Z);
    planeGeom.rotateX(-Math.PI / 2);

    // ─── Custom shader: GPU noise displacement + procedural grid ───────────
    const uniforms = {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(0xccff00) },
      uColorSecondary: { value: new THREE.Color(0xffffff) },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      side: THREE.FrontSide,
      vertexShader: /* glsl */ `
        uniform float uTime;
        varying vec2 vUv;
        varying float vElev;

        // Hash → smooth 0..1 random
        float hash(vec2 p) {
          p = fract(p * vec2(443.897, 441.423));
          p += dot(p, p.yx + 19.19);
          return fract((p.x + p.y) * p.x);
        }

        // Value noise with quintic (Perlin) smoothstep — C² continuous, no kinks
        float vnoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          // Quintic: 6t^5 - 15t^4 + 10t^3
          vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y) * 2.0 - 1.0;
        }

        void main() {
          vUv = uv;
          vec3 pos = position;

          // Slow drift, single low-frequency octave for calm waves
          float t = uTime * 0.13;
          float n = vnoise(vec2(pos.x * 0.10 + t * 0.6, pos.z * 0.09 - t * 0.35));

          // Distance fade — foreground gentle, far edge near-flat
          float distanceFade = smoothstep(-14.0, 2.0, pos.z);

          // Clamp displacement to prevent any rogue spikes
          float disp = clamp(n * 0.55, -0.7, 0.7) * distanceFade;

          pos.y += disp;
          vElev = disp;
          gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        uniform vec3 uColorSecondary;
        varying vec2 vUv;
        varying float vElev;

        float grid(vec2 uv, float lineWidth, float cellSize) {
          vec2 gridUV = fract(uv * cellSize);
          vec2 distFromEdge = min(gridUV, 1.0 - gridUV);
          vec2 widthV = vec2(lineWidth) * fwidth(uv * cellSize);
          vec2 line = 1.0 - smoothstep(vec2(0.0), widthV, distFromEdge);
          return max(line.x, line.y);
        }

        void main() {
          float major = grid(vUv, 1.5, 22.0);
          float minor = grid(vUv, 1.0, 88.0) * 0.35;
          float lines = max(major, minor);

          vec3 colorBase = mix(uColor, uColorSecondary, smoothstep(0.0, 0.85, vUv.y));
          float elevGlow = clamp(vElev * 1.4 + 0.3, 0.0, 1.0);
          colorBase = mix(colorBase, uColor, elevGlow * 0.55);

          float horizonFade = smoothstep(0.92, 0.50, vUv.y);
          float sideFadeL = smoothstep(0.0, 0.22, vUv.x);
          float sideFadeR = smoothstep(1.0, 0.78, vUv.x);
          float centerFalloff = 1.0 - smoothstep(0.0, 0.55, abs(vUv.x - 0.5));
          centerFalloff = mix(0.35, 1.0, centerFalloff);
          float fade = horizonFade * sideFadeL * sideFadeR * centerFalloff;

          float alpha = lines * fade * 0.6;
          if (alpha < 0.001) discard;
          gl_FragColor = vec4(colorBase, alpha);
        }
      `,
    });

    const floor = new THREE.Mesh(planeGeom, material);
    floor.position.y = -0.5;
    floor.position.z = -6.5;
    scene.add(floor);

    // ─── Animation loop with viewport visibility pause ─────────────────────
    const clock = new THREE.Clock();
    let rafId;
    let isVisible = true;

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      if (!isVisible || document.hidden) return;
      uniforms.uTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
    };
    rafId = requestAnimationFrame(animate);

    // ─── Pause when hero is off-screen ──────────────────────────────────────
    const observer = new IntersectionObserver(
      (entries) => { isVisible = entries[0].isIntersecting; },
      { threshold: 0 }
    );
    observer.observe(mount);

    // ─── Resize handling (debounced) ────────────────────────────────────────
    let resizeT;
    const handleResize = () => {
      clearTimeout(resizeT);
      resizeT = setTimeout(() => {
        if (!mount) return;
        const w = mount.clientWidth;
        const h = mount.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }, 150);
    };
    window.addEventListener("resize", handleResize);

    // ─── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeT);
      planeGeom.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="lp-terrain" aria-hidden="true" />;
}
